import React, { useContext, useEffect, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { ColorContext } from 'App';
import { Card, Button, Form, Row, Col, Spinner, Stack, ListGroup, Alert } from 'react-bootstrap';
import { fusionGetLicense, fusionVerifyLicense } from 'services/policy/license.service';

export const LicenseInformation = ({
  setShowLicenseInformation,
  inline,
  activationCode = '0',
  callback,
}) => {
  const [showKey, setShowKey] = useState(false);
  const [licenseDetails, setLicenseDetails] = useState();
  const [userInputKey, setUserInputKey] = useState('');
  const [validationError, setValidationError] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [userLocation, setUserLocation] = useState({
    lat: null,
    lon: null,
  });
  const [orgId, setOrgID] = useState('');

  const theme = useContext(ColorContext);

  // Fetch existing license / activation info
  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const res = await fusionGetLicense(activationCode);

        if (res.data.subscriptionInfo) {
          if (callback) callback();
          setLicenseDetails(res.data.subscriptionInfo[0]);
        } else if (res.data.activation) {
          setOrgID(res.data.activation[0].gen_wp_id);
        }
      } catch (err) {
        // optionally handle/log error
      } finally {
        setFetching(false);
      }
    };

    fetchLicense();
  }, [activationCode, callback]);

  // Get user location (required for verification)
  useEffect(() => {
    if (!navigator.geolocation) {
      alert('Geolocation unavailable in this browser');
      return;
    }

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'granted' || result.state === 'prompt') {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setUserLocation({
                lat: position.coords.latitude,
                lon: position.coords.longitude,
              });
            },
            (err) => {
              console.warn(`ERROR(${err.code}): ${err.message}`);
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            },
          );
        } else {
          alert(
            'Please enable location access and refresh this page, otherwise authorization will not be successful.',
          );
        }
      })
      .catch(() => {
        // permissions API not available, silently ignore
      });
  }, []);

  const verifyLicense = async () => {
    if (userInputKey.length !== 29) return;

    if (!userLocation.lat || !userLocation.lon) {
      setValidationError({
        message: 'Location services not enabled. Please enable location services and try again.',
      });
      return;
    }

    try {
      setFetching(true);
      const res = await fusionVerifyLicense(
        userInputKey,
        activationCode,
        userLocation.lat,
        userLocation.lon,
      );

      if (res.status === 200) {
        setValidationError(false);
        setLicenseDetails(res.data.subscriptionInfo[0]);
        if (callback) callback();
      }
    } catch (err) {
      if (err.response && err.response.status === 500) {
        setValidationError({ message: 'Activation failed' });
      } else {
        setValidationError({ message: 'Invalid License Key' });
      }
    } finally {
      setFetching(false);
    }
  };

  const stringToLicense = (str) => {
    // add hyphens every 5 chars
    let license = '';
    const cleaned = str.replace(/-/g, '');
    for (let i = 0; i < cleaned.length; i++) {
      if (i !== 0 && i % 5 === 0) license += '-';
      license += cleaned[i];
    }
    return license;
  };

  return (
    <Card className={inline ? 'border-0' : ''} style={{ backgroundColor: theme.base }}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">License Information</Card.Title>
        {!inline && (
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => setShowLicenseInformation(false)}
          >
            ×
          </Button>
        )}
      </Card.Header>

      <Card.Body>
        {fetching && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Fetching license details...</span>
          </div>
        )}

        {!fetching && !licenseDetails && (
          <Stack gap={3}>
            <Row>
              <Col xs={12} md="auto">
                <strong>Organization ID:</strong> <span className="text-muted">{orgId}</span>
              </Col>
            </Row>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                verifyLicense();
              }}
            >
              <Row className="align-items-center gy-2 gx-3">
                <Col xs={12} md="auto">
                  <Form.Label className="fw-semibold mb-0">License Key</Form.Label>
                </Col>

                <Col xs={12} md={6} lg={5}>
                  <Form.Control
                    type="text"
                    maxLength={29}
                    value={stringToLicense(userInputKey)}
                    onChange={(e) => setUserInputKey(e.target.value)}
                    className="font-monospace"
                    placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                  />
                </Col>

                <Col xs={12} md="auto">
                  <Button type="submit" variant="primary" disabled={userInputKey.length !== 29}>
                    Verify
                  </Button>
                </Col>
              </Row>
            </Form>

            {validationError && (
              <Alert variant="danger" className="mb-0">
                {validationError.message}
              </Alert>
            )}
          </Stack>
        )}

        {!fetching && licenseDetails && (
          <Stack gap={3} className="mt-3">
            <Row>
              <Col xs={12} md={8}>
                <div className="d-flex flex-wrap gap-1">
                  <span className="fw-semibold">Organization ID:</span>
                  <span className="text-muted">{licenseDetails.gen_wp_id}</span>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                  <span className="fw-semibold">License Key:</span>
                  <span className="text-muted">
                    {showKey ? licenseDetails.license_key : '*****-*****-*****-*****-*****'}
                  </span>
                  <Button
                    variant="outline-light"
                    size="sm"
                    className="d-inline-flex align-items-center"
                    onClick={() => setShowKey((prev) => !prev)}
                  >
                    {showKey ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>

                <div className="d-flex flex-wrap gap-1 mt-2">
                  <span className="fw-semibold">License Expiration:</span>
                  <span className="text-muted">{licenseDetails.license_expiration}</span>
                </div>
              </Col>
            </Row>

            <div>
              <div className="fw-semibold mb-2">License Details</div>
              <ListGroup className="max-h-md max-w-lg">
                <ListGroup.Item style={{ backgroundColor: theme.primary }}>
                  Base Fusion: {licenseDetails.fusion_activations} /{' '}
                  {licenseDetails.fusion_max_activations} licenses used
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: theme.primary }}>
                  Custom Weather: {licenseDetails.custom_weather ? 'Enabled' : 'Disabled'}
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: theme.primary }}>
                  Basic Systems: {licenseDetails.basic_system} available
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: theme.primary }}>
                  Advanced Systems: {licenseDetails.advanced_system} available
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: theme.primary }}>
                  Connected Devices: {licenseDetails.connected_devices} available
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: theme.primary }}>
                  Connected Cameras: {licenseDetails.connected_cameras} available
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: theme.primary }}>
                  SUSP: {licenseDetails.susp ? 'Enabled' : 'Disabled'}
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: theme.primary }}>
                  Critical Mention: {licenseDetails.critical_mention} available
                </ListGroup.Item>
              </ListGroup>
            </div>
          </Stack>
        )}
      </Card.Body>
    </Card>
  );
};
